import React, { useState, useRef } from 'react';
import { DocumentTextIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface CertificateUploaderProps {
    certificateUrl?: string;
    certificateName?: string;
    onUpload: (file: File) => Promise<void>;
    onRemove?: () => Promise<void>;
    maxSizeMB?: number;
}

/**
 * Компонент для загрузки сертификата (PDF)
 */
export const CertificateUploader: React.FC<CertificateUploaderProps> = ({
    certificateUrl,
    certificateName,
    onUpload,
    onRemove,
    maxSizeMB = 50,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            await handleFile(files[0]);
        }
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            await handleFile(files[0]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFile = async (file: File) => {
        setError(null);

        // Проверка типа файла
        if (file.type !== 'application/pdf') {
            setError('Недопустимый формат файла. Разрешен только PDF');
            return;
        }

        // Проверка размера
        const maxSize = maxSizeMB * 1024 * 1024;
        if (file.size > maxSize) {
            setError(`Размер файла не должен превышать ${maxSizeMB} МБ`);
            return;
        }

        setUploading(true);
        try {
            await onUpload(file);
        } catch (err: any) {
            setError(err.message || 'Ошибка при загрузке файла');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!onRemove) return;
        
        setUploading(true);
        try {
            await onRemove();
        } catch (err: any) {
            setError(err.message || 'Ошибка при удалении файла');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Сертификат качества
            </label>

            {certificateUrl ? (
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                            <DocumentTextIcon className="h-8 w-8 text-blue-500 mr-3" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {certificateName || 'Сертификат'}
                                </p>
                                <p className="text-xs text-gray-500">PDF документ</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                            <a
                                href={certificateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Открыть сертификат"
                            >
                                <ArrowDownTrayIcon className="h-5 w-5" />
                            </a>
                            {onRemove && (
                                <button
                                    onClick={handleRemove}
                                    disabled={uploading}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Удалить сертификат"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileInput}
                        className="hidden"
                    />
                    
                    {uploading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600">Загрузка...</span>
                        </div>
                    ) : (
                        <>
                            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                            <p className="text-sm text-gray-600 mb-1">
                                Перетащите PDF файл или нажмите для выбора
                            </p>
                            <p className="text-xs text-gray-500">Формат: PDF</p>
                            <p className="text-xs text-gray-500">Максимальный размер: {maxSizeMB} МБ</p>
                        </>
                    )}
                </div>
            )}

            {error && (
                <div className="mt-2 text-sm text-red-600">
                    {error}
                </div>
            )}
        </div>
    );
};

